import { useQueryClient } from "@tanstack/react-query"
import { useEffect, useState } from "react"

import { ListItemStack } from "@molecules"
import GenericForm from "@molecules/Form/GenericForm/GenericForm"
import ListItemDivider from "@molecules/ListItem/ListItemDivider/ListItemDivider"

import { Settings } from "@templates/index"

import {
    JobsManagerNotificationTopics,
    JobsNotificationTopics,
} from "@pages/Settings/SettingsNotifications/SettingsNotification.types"
import {
    notificationPreferenceQueryKey,
    useNotificationPreferencesQuery,
} from "@pages/Settings/SettingsNotifications/SettingsNotifications.store"
import NotificationProperty from "@pages/Settings/SettingsNotifications/components/NotificationProperty/NotificationProperty"

import { NOTIFICATION_ENDPOINTS } from "@endpoints/notification"

export default function JobsManagerNotificationsForm() {
    const [shouldSetDefaultFormData, setShouldSetDefaultFormData] = useState<boolean>(false)

    const { topicPreference: jobsNotificationPreference, isLoading: isLoadingData } =
        useNotificationPreferencesQuery<JobsNotificationTopics>("jobs")

    const queryClient = useQueryClient()

    useEffect(() => {
        if (!isLoadingData && !!jobsNotificationPreference) {
            setShouldSetDefaultFormData(true)
        }
    }, [isLoadingData])

    const onMutationSuccess = () => {
        void queryClient.invalidateQueries({ queryKey: notificationPreferenceQueryKey })
    }

    const onAfterFormValuesSet = () => {
        setShouldSetDefaultFormData(false)
    }

    return (
        <GenericForm<
            JobsManagerNotificationTopics,
            JobsManagerNotificationTopics,
            Error,
            JobsManagerNotificationTopics
        >
            formConfig={{
                defaultValues: undefined,
            }}
            mutationConfig={{
                genericErrorMessage: "Could not update notification preference",
                genericSuccessMessage: "Notification preference updated",
                method: "PATCH",
                endpoint: NOTIFICATION_ENDPOINTS.UPDATE_NOTIFICATION_PREFERENCES,
            }}
            onMutationSuccess={onMutationSuccess}
            formValues={shouldSetDefaultFormData ? jobsNotificationPreference.manager : undefined}
            onAfterFormValuesSet={onAfterFormValuesSet}
            isAutoSave={true}
        >
            {() => {
                return (
                    <Settings.NotificationConfigPage.Wrapper>
                        <ListItemStack>
                            <NotificationProperty<JobsManagerNotificationTopics>
                                label="Job created"
                                supportText="Notify me when a teammate creates a job."
                                emailCheckboxName="enable_job_notification_new_job_request_email"
                                smsCheckboxName="enable_job_notification_new_job_request_sms"
                                isLoading={isLoadingData}
                            />
                            <ListItemDivider />
                            <NotificationProperty<JobsManagerNotificationTopics>
                                label="Job started"
                                supportText="Notify me when someone starts a job."
                                emailCheckboxName="enable_job_notification_job_started_email"
                                smsCheckboxName="enable_job_notification_job_started_sms"
                                isLoading={isLoadingData}
                            />
                            <ListItemDivider />
                            <NotificationProperty<JobsManagerNotificationTopics>
                                label="Job quick-paused"
                                supportText="Notify me when someone quick-pauses a job."
                                emailCheckboxName="enable_job_notification_job_paused_email"
                                smsCheckboxName="enable_job_notification_job_paused_sms"
                                isLoading={isLoadingData}
                            />
                            <ListItemDivider />
                            <NotificationProperty<JobsManagerNotificationTopics>
                                label="Job placed on-hold"
                                supportText="Notify me when someone places on-hold a job."
                                emailCheckboxName="enable_job_notification_job_placed_on_hold_email"
                                smsCheckboxName="enable_job_notification_job_placed_on_hold_sms"
                                isLoading={isLoadingData}
                            />
                            <ListItemDivider />
                            <NotificationProperty<JobsManagerNotificationTopics>
                                label="Job resumed"
                                supportText="Notify me when someone resumes a job."
                                emailCheckboxName="enable_job_notification_job_resumed_email"
                                smsCheckboxName="enable_job_notification_job_resumed_sms"
                                isLoading={isLoadingData}
                            />
                            <ListItemDivider />
                            <NotificationProperty<JobsManagerNotificationTopics>
                                label="Job completed"
                                supportText="Notify me when someone completes a job."
                                emailCheckboxName="enable_job_notification_job_completed_email"
                                smsCheckboxName="enable_job_notification_job_completed_sms"
                                isLoading={isLoadingData}
                            />
                            <ListItemDivider />
                            <NotificationProperty<JobsManagerNotificationTopics>
                                label="Job summary updated"
                                supportText="Notify me when someone updates the summary of a job."
                                emailCheckboxName="enable_job_notification_job_summary_updated_email"
                                smsCheckboxName="enable_job_notification_job_summary_updated_sms"
                                isLoading={isLoadingData}
                            />
                        </ListItemStack>
                    </Settings.NotificationConfigPage.Wrapper>
                )
            }}
        </GenericForm>
    )
}
